import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { GroupedResults, TResultsBot } from '@core/interfaces/TResultsItem';

import { ConfirmationModalComponent } from '../../components/modals/confirmation-modal/confirmation-modal.component';
import { ResultDataLoaderService } from '../result-data-loader.service';
import {
  ListGroupComponent,
  ListItemComponent,
} from '@shared/components/content-panel';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { ResultsService } from '@shared/services/results.service';

@Component({
  selector: 'app-results-list',
  templateUrl: './results-list.component.html',
  styleUrl: './results-list.component.scss',
  imports: [
    MatIcon,
    MatMenuModule,
    SpinnerComponent,
    ListGroupComponent,
    ListItemComponent,
  ],
})
export class ResultsListComponent implements OnChanges {
  @Input() groupedResults: GroupedResults[] = [];
  @Input() selectedOption = 'byDate';
  @Input() canSelect = false;
  @Output() itemSelected = new EventEmitter<TResultsBot>();
  @Output() multipleItemsSelected = new EventEmitter<{ checked: boolean; item: TResultsBot }>();
  @Output() itemDeleted = new EventEmitter<string>();

  protected selectedItem: TResultsBot | null = null;
  private checkedIds = new Set<string>();

  constructor(
    private _resultsService: ResultsService,
    private _resultDataLoaderService: ResultDataLoaderService,
    private _dialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.canSelect && !this.canSelect) {
      this.checkedIds.clear();
    }
  }

  onGroupToggle(group: GroupedResults, expanded: boolean): void {
    this._resultDataLoaderService.updateGroup(group.detailsQuery, {
      isOpened: expanded,
    });
    if (expanded && this.selectedOption !== 'byFavorites') {
      this.loadGroupItems(group);
    }
  }

  selectItem(item: TResultsBot): void {
    this.itemSelected.emit(item);
    this.selectedItem = item;
  }

  deleteItem(item: TResultsBot): void {
    const dialogRef = this._dialog.open(ConfirmationModalComponent, {
      data: { text: `Are you sure you want to delete "${item.name || 'this conversation'}"?` },
    });
    const sub = dialogRef.componentInstance.actionCallback.subscribe(() => {
      dialogRef.close();
      this._resultsService.cancelableDeleteConversation(item);
      this._resultDataLoaderService.removeConversationFromGroups(item._id);
      this.itemDeleted.emit(item._id);
      sub.unsubscribe();
    });
  }

  getDisplayName(item: TResultsBot): string {
    if (!item.name) return 'New chat';

    if (item.name.trim().startsWith('{') && item.name.includes('"type":"quiz"')) {
      try {
        const parsed = JSON.parse(item.name);
        if (parsed.type === 'quiz' && parsed.data?.question) {
          const q = parsed.data.question;
          return q.length > 60 ? `Quiz: ${q.substring(0, 57)}...` : `Quiz: ${q}`;
        }
      } catch {
        return item.name;
      }
    }

    return item.name;
  }

  isItemChecked(item: TResultsBot): boolean {
    return this.checkedIds.has(item._id);
  }

  onItemCheckedChange(item: TResultsBot, checked: boolean): void {
    if (checked) {
      this.checkedIds.add(item._id);
    } else {
      this.checkedIds.delete(item._id);
    }
    this.multipleItemsSelected.emit({ checked, item });
  }

  private loadGroupItems(group: GroupedResults): void {
    this._resultDataLoaderService.setGroupLoading(group.detailsQuery, true);
    this._resultsService
      .fetchResults(1, 200, group.detailsQuery)
      .subscribe(response => {
        this._resultDataLoaderService.setGroupItems(group.detailsQuery, response.results);
      });
  }
}
